import { ReadonlyVec2 } from 'gl-matrix';
import { useCallback } from 'react';
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { DoseId } from '~/domain/changes/ids';
import {
    DEFAULT_DOSE_DISPLAY_SETTINGS,
    DoseDisplaySettings,
    doseDisplaySettingsState,
} from '~/state/atoms/dose/doseDisplaySettings';
import {
    isValidColorwashRange,
    isValidDoseScale,
    isValidNormalizationGy,
} from '~/utils/dose/doseDisplayValidation';

export type DoseDisplayPatch = Partial<DoseDisplaySettings>;

// Mutable during assembly; the store holds the readonly form.
type MutableDoseDisplayPatch = {
    -readonly [K in keyof DoseDisplaySettings]?: DoseDisplaySettings[K];
};

/**
 * The single validation gate for the display store: every write in this
 * module goes through it, so there is one place where the SR-WOR-H-4-1
 * validity rule is enforced and no path can accept a value another path
 * would drop. Invalid fields are dropped; valid ones are applied.
 */
function validatePatch(patch: DoseDisplayPatch): DoseDisplayPatch {
    const clean: MutableDoseDisplayPatch = {};
    if (patch.doseScale !== undefined && isValidDoseScale(patch.doseScale)) {
        clean.doseScale = patch.doseScale;
    }
    if (patch.normalizationValueGy !== undefined) {
        const v = patch.normalizationValueGy;
        // null is valid — it means "normalize against the dose maximum".
        if (v === null || isValidNormalizationGy(v)) {
            clean.normalizationValueGy = v;
        }
    }
    if (
        patch.selectedRange !== undefined &&
        isValidColorwashRange(patch.selectedRange)
    ) {
        // Copy into a plain tuple: a caller reusing its vec2 buffer must not
        // be able to mutate stored state through the reference it handed in.
        // Frozen, because Recoil's dev-mode deep freeze does not reach values
        // held inside a Map — without this the committed entries stay
        // writable at runtime and the store's doc overclaims.
        clean.selectedRange = Object.freeze([
            patch.selectedRange[0],
            patch.selectedRange[1],
        ]) as ReadonlyVec2;
    }
    return clean;
}

/** How many fields the caller actually asked to set (undefined = not asked). */
function requestedFieldCount(patch: DoseDisplayPatch): number {
    return (Object.keys(patch) as (keyof DoseDisplaySettings)[]).filter(
        (key) => patch[key] !== undefined
    ).length;
}

/**
 * Imperative setter for the per-dose display store — updates one dose's
 * settings through {@link validatePatch}, dropping invalid fields. Used
 * where the doseId is not fixed at render time (e.g. applying prediction
 * results to several doses at once). UI components with a single selected
 * dose should prefer {@link useDoseDisplaySettings}.
 *
 * Returns `true` only when every requested field was accepted, so a caller
 * can surface a rejection rather than leaving the user with an input that
 * looks applied.
 *
 * NB: writing `selectedRange` for the *currently shown* dose through this
 * setter updates the store only. {@link useDose} restores the engine
 * colorwash on a dose switch, not on a store write, so such a caller must
 * resync the engine itself.
 */
export function useSetDoseDisplaySettings(): (
    doseId: DoseId,
    patch: DoseDisplayPatch
) => boolean {
    const setMap = useSetRecoilState(doseDisplaySettingsState);
    return useCallback(
        (doseId: DoseId, patch: DoseDisplayPatch): boolean => {
            const clean = validatePatch(patch);
            const accepted = Object.keys(clean).length;
            if (accepted === 0) {
                return false;
            }
            setMap((prev) => {
                const next = new Map(prev);
                const current =
                    prev.get(doseId) ?? DEFAULT_DOSE_DISPLAY_SETTINGS;
                next.set(doseId, { ...current, ...clean });
                return next;
            });
            return accepted === requestedFieldCount(patch);
        },
        [setMap]
    );
}

/**
 * Drops one dose's display settings. Call when the dose itself is deleted:
 * a `DoseId` is a per-session uuid, so a surviving entry can never be read
 * back and only carries a deleted dose's prescription forward.
 */
export function useRemoveDoseDisplaySettings(): (doseId: DoseId) => void {
    const setMap = useSetRecoilState(doseDisplaySettingsState);
    return useCallback(
        (doseId: DoseId) => {
            setMap((prev) => {
                if (!prev.has(doseId)) {
                    return prev;
                }
                const next = new Map(prev);
                next.delete(doseId);
                return next;
            });
        },
        [setMap]
    );
}

/** Clears every dose's display settings — used when unloading a patient. */
export function useResetDoseDisplaySettings(): () => void {
    return useResetRecoilState(doseDisplaySettingsState);
}

export interface DoseDisplaySettingsApi {
    /** Committed settings for the dose, defaults applied. */
    readonly settings: DoseDisplaySettings;
    /**
     * Each setter validates and, on an invalid value, is a no-op returning
     * `false` — the previous valid value is retained and the caller is
     * expected to surface the rejection rather than leave its input showing
     * a number the store never accepted. This is the UI-layer home of the
     * rules that SR-WOR-H-4-1 / SRV-WOR-H-4-1-1 describe.
     */
    setDoseScale: (value: number) => boolean;
    setNormalizationValueGy: (value: number) => boolean;
    setSelectedRange: (range: ReadonlyVec2) => boolean;
}

/**
 * Reads and writes the per-dose display settings (colorwash range, scale,
 * normalization) held in the UI layer keyed by {@link DoseId}. Returns
 * defaults for a dose with no stored settings; a `null` doseId (nothing
 * selected) yields the defaults with no-op setters that report `false`.
 */
export function useDoseDisplaySettings(
    doseId: DoseId | null
): DoseDisplaySettingsApi {
    const map = useRecoilValue(doseDisplaySettingsState);
    const set = useSetDoseDisplaySettings();
    const settings =
        (doseId && map.get(doseId)) || DEFAULT_DOSE_DISPLAY_SETTINGS;

    const setDoseScale = useCallback(
        (value: number): boolean =>
            doseId !== null && set(doseId, { doseScale: value }),
        [doseId, set]
    );

    const setNormalizationValueGy = useCallback(
        (value: number): boolean =>
            doseId !== null && set(doseId, { normalizationValueGy: value }),
        [doseId, set]
    );

    const setSelectedRange = useCallback(
        (range: ReadonlyVec2): boolean =>
            doseId !== null && set(doseId, { selectedRange: range }),
        [doseId, set]
    );

    return {
        settings,
        setDoseScale,
        setNormalizationValueGy,
        setSelectedRange,
    };
}
